import React, { useState } from "react";
import { usersApi } from "../api/users";
import { ApiError } from "../api/utils";
import { SignUpParams, User } from "../modules/users/types";
import { generateWorkspaceSlug } from "../utils/generateWorkspaceSlug";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface SignUpProps {
  onSuccess: (user: User) => void;
  onError: (error: string) => void;
}

const SignUp: React.FunctionComponent<SignUpProps> = ({
  onSuccess,
  onError,
}) => {
  const [formData, setFormData] = useState<SignUpParams>({
    email: "",
    password: "",
    passwordConfirmation: "",
    workspaceName: "",
    workspaceSlug: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await usersApi.signUp({
        email: formData.email,
        password: formData.password,
        passwordConfirmation: formData.passwordConfirmation,
        workspaceName: formData.workspaceName,
        workspaceSlug: formData.workspaceSlug,
      });
      onSuccess(user);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        onError(err.message);
      } else {
        onError("An error occurred during sign up");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="sign-up-email">Email</Label>
        <Input
          id="sign-up-email"
          type="email"
          value={formData.email}
          onChange={(e) =>
            setFormData({ ...formData, email: e.target.value })
          }
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="sign-up-password">Password</Label>
        <Input
          id="sign-up-password"
          type="password"
          value={formData.password}
          onChange={(e) =>
            setFormData({ ...formData, password: e.target.value })
          }
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="sign-up-password-confirmation">Password Confirmation</Label>
        <Input
          id="sign-up-password-confirmation"
          type="password"
          value={formData.passwordConfirmation}
          onChange={(e) =>
            setFormData({
              ...formData,
              passwordConfirmation: e.target.value,
            })
          }
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="sign-up-workspace-name">Workspace Name</Label>
        <Input
          id="sign-up-workspace-name"
          type="text"
          value={formData.workspaceName}
          onChange={(e) => {
            const workspaceName = e.target.value;
            setFormData({
              ...formData,
              workspaceName,
              workspaceSlug: generateWorkspaceSlug(workspaceName),
            });
          }}
          required
        />
      </div>
      <input type="hidden" value={formData.workspaceSlug} />
      <Button type="submit" disabled={loading} className="w-full">
        {loading ? "Signing up..." : "Sign Up"}
      </Button>
    </form>
  );
};

export default SignUp;

